import { useSyncExternalStore } from 'react';
import {
  checkAddedUploadStart,
  checkAddedVideo,
  type AddedAssetTheme,
  type AddedVideoWarning,
} from 'fym-shared/src/music24/addedAssets';
import type { CreateAssetPublishBody } from 'fym-shared/src/music24/oneOffPublish';
import { addedAssetsApi } from '../../../../redux/api/addedAssetsApi';
import { fymAPI } from '../../../../redux/api/fymAPI';
import type { PostVideoMeasures, SerializedKitAsset } from '../../../../redux/api/fymAPI.types';
import type { StoreType } from '../../../../redux/store';
import {
  MasterPartsStop,
  uploadMasterParts,
  type UploadedMasterPart,
} from '../../../smartLinks/catalog/song/masterUploadParts';
import { measureVideoFile, pickerUploadType } from '../composer/picker/pickerUpload';
import { studioPublishBody, type StudioPublishChoice } from '../publish/studioPublishChoice';
import { addedErrorKind, isAddedUploadStopped, type AddedErrorKind } from './addedErrors';

/**
 * The upload of "Add yours" (spec 2026-10-09 §3.3, design a4). It lives outside React, so the
 * upload goes on when the dialog closes ("You can close this window") and the dialog shows it again
 * when it opens. One upload per song and asset type.
 *
 * 1. The browser measures the video and checks the rules it can (type, size, the Canvas length and
 *    shape). A refusal stops before a byte goes up.
 * 2. The parts go straight to S3 (`uploadMasterParts`, the Composer pattern of
 *    `usePickerVideoSender`). Stop aborts the part on the way and the upload on the server.
 * 3. The complete call makes the ready kit asset version.
 * 4. With "Also publish it on YouTube" on, the publish request goes out (the publish dialog rules:
 *    release day or now, "made for kids").
 */
export type AddedUploadPhase = 'measuring' | 'uploading' | 'checking' | 'publishing' | 'done' | 'failed';

export interface AddedUploadState {
  key: string;
  accountId: number;
  catalogItemUuid: string;
  themeId: AddedAssetTheme;
  fileName: string;
  sizeBytes: number;
  sentBytes: number;
  measures: PostVideoMeasures | null;
  /** The warnings of the browser check, then the warnings of the server. */
  warnings: AddedVideoWarning[];
  phase: AddedUploadPhase;
  error: AddedErrorKind | null;
  asset: SerializedKitAsset | null;
  /** "Also publish it on YouTube" (null: no switch). Read at the end, so a change during the upload counts. */
  publishChoice: StudioPublishChoice | null;
  releaseDate: string | null;
}

export function addedUploadKey(catalogItemUuid: string, themeId: string): string {
  return `${catalogItemUuid}:${themeId}`;
}

const uploads = new Map<string, AddedUploadState>();
const stops = new Map<string, () => void>();
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

function patch(key: string, change: Partial<AddedUploadState>): void {
  const before = uploads.get(key);
  if (before !== undefined) {
    uploads.set(key, { ...before, ...change });
    emit();
  }
}

export function getAddedUpload(key: string): AddedUploadState | null {
  return uploads.get(key) ?? null;
}

/** True while the upload works (not done and not failed). */
export function isAddedUploadRunning(state: AddedUploadState | null): boolean {
  return state !== null && state.phase !== 'done' && state.phase !== 'failed';
}

/** Forgets a finished or failed upload (the dialog does it when it closes on one). */
export function clearAddedUpload(key: string): void {
  if (!isAddedUploadRunning(getAddedUpload(key)) && uploads.delete(key)) {
    emit();
  }
}

export function setAddedUploadPublishChoice(key: string, publishChoice: StudioPublishChoice | null): void {
  patch(key, { publishChoice });
}

/** Stops a running upload: the part on the way, and the upload on the server. The entry goes. */
export function stopAddedUpload(key: string): void {
  stops.get(key)?.();
  stops.delete(key);
  if (uploads.delete(key)) {
    emit();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useAddedUpload(catalogItemUuid: string, themeId: string): AddedUploadState | null {
  const key = addedUploadKey(catalogItemUuid, themeId);
  return useSyncExternalStore(
    subscribe,
    () => uploads.get(key) ?? null,
    () => null
  );
}

/** Sends a publish request; when another revision is live on YouTube, the new one replaces it (owner decision 2026-10-09). */
async function publishAddedVideo(dispatch: StoreType['dispatch'], accountId: number, body: CreateAssetPublishBody) {
  try {
    await dispatch(fymAPI.endpoints.createAssetPublish.initiate({ accountId, body })).unwrap();
  } catch (error) {
    const cause = (error as { m24Limit?: { code?: string; reason?: string } } | null)?.m24Limit;
    if (cause?.code === 'M24_PUBLISH_REPLACE_CHOICE_REQUIRED' && typeof cause.reason === 'string') {
      await dispatch(
        fymAPI.endpoints.createAssetPublish.initiate({
          accountId,
          body: { ...body, replacesPublishUuid: cause.reason },
        })
      ).unwrap();
      return;
    }
    throw error;
  }
}

export interface StartAddedUploadInput {
  accountId: number;
  catalogItemUuid: string;
  themeId: AddedAssetTheme;
  file: File;
  songDurationSec: number | null;
  releaseDate: string | null;
  publishChoice: StudioPublishChoice | null;
}

export interface AddedUploadDeps {
  dispatch: StoreType['dispatch'];
  /** The browser measure of a video; a test passes its own. */
  measure?: (file: Blob) => Promise<PostVideoMeasures | null>;
}

function fail(key: string, error: unknown): void {
  if (isAddedUploadStopped(error) || !uploads.has(key)) {
    return;
  }
  patch(key, { phase: 'failed', error: addedErrorKind(error) });
}

/** Starts the upload of one file; a running upload of the same song and asset type stops first. */
export async function startAddedUpload(deps: AddedUploadDeps, input: StartAddedUploadInput): Promise<void> {
  const { dispatch, measure = measureVideoFile } = deps;
  const { accountId, catalogItemUuid, themeId, file } = input;
  const key = addedUploadKey(catalogItemUuid, themeId);
  stopAddedUpload(key);
  const contentType = pickerUploadType(file) ?? '';
  uploads.set(key, {
    key,
    accountId,
    catalogItemUuid,
    themeId,
    fileName: file.name,
    sizeBytes: file.size,
    sentBytes: 0,
    measures: null,
    warnings: [],
    phase: 'measuring',
    error: null,
    asset: null,
    publishChoice: input.publishChoice,
    releaseDate: input.releaseDate,
  });
  emit();
  let alive = true;
  stops.set(key, () => {
    alive = false;
  });

  const startRefusal = checkAddedUploadStart(themeId, { contentType, sizeBytes: file.size });
  if (startRefusal !== null) {
    stops.delete(key);
    patch(key, { phase: 'failed', error: startRefusal });
    return;
  }
  const measures = await measure(file);
  if (!alive) {
    return;
  }
  // The browser checks the rules it can read; a file it cannot measure goes to the server, which decides.
  if (measures !== null) {
    const check = checkAddedVideo(
      themeId,
      {
        contentType,
        sizeBytes: file.size,
        durationSec: measures.durationSeconds,
        width: measures.width,
        height: measures.height,
      },
      input.songDurationSec
    );
    if (!check.ok) {
      stops.delete(key);
      patch(key, { phase: 'failed', measures, error: check.reason });
      return;
    }
    patch(key, { measures, warnings: check.warnings });
  }

  const song = { accountId, catalogItemUuid };
  let ids: { uploadId: string; key: string } | null = null;
  const abortOnServer = () => {
    if (ids !== null) {
      dispatch(addedAssetsApi.endpoints.abortAddedUpload.initiate({ ...song, ...ids }))
        .unwrap()
        .catch(console.error);
    }
  };
  try {
    const created = await dispatch(
      addedAssetsApi.endpoints.startAddedUpload.initiate({
        ...song,
        themeId,
        fileName: file.name,
        contentType,
        sizeBytes: file.size,
      })
    ).unwrap();
    ids = { uploadId: created.uploadId, key: created.key };
    if (!alive) {
      abortOnServer();
      return;
    }
    const stop = new MasterPartsStop();
    stops.set(key, () => {
      alive = false;
      stop.stop();
      abortOnServer();
    });
    patch(key, { phase: 'uploading' });
    let parts: UploadedMasterPart[];
    try {
      parts = await uploadMasterParts({
        file,
        parts: created.parts,
        partSizeBytes: created.partSizeBytes,
        stop,
        onProgress: (sentBytes) => patch(key, { sentBytes }),
      });
    } catch (failure) {
      if (!isAddedUploadStopped(failure)) {
        abortOnServer();
      }
      throw failure;
    }
    // Nothing stops the complete call: the server saves the file.
    stops.delete(key);
    patch(key, { phase: 'checking', sentBytes: file.size });
    const done = await dispatch(
      addedAssetsApi.endpoints.completeAddedUpload.initiate({ ...song, ...ids, parts })
    ).unwrap();
    const latest = getAddedUpload(key);
    const choice = latest?.publishChoice ?? null;
    const body =
      choice === null ? null : studioPublishBody(choice, { uuid: done.asset.uuid, themeId }, input.releaseDate);
    const warnings = done.warnings.length > 0 ? done.warnings : (latest?.warnings ?? []);
    if (body !== null) {
      patch(key, { phase: 'publishing', asset: done.asset, warnings });
      // The video is added; a failed publish shows in the Kit badges and the user can publish there.
      await publishAddedVideo(dispatch, accountId, body).catch(console.error);
    }
    patch(key, { phase: 'done', asset: done.asset, warnings });
  } catch (error) {
    stops.delete(key);
    fail(key, error);
  }
}
